(() => {
  if (customElements.get('led-cart-preview')) return;

  class LEDCartPreview extends HTMLElement {
    connectedCallback() {
      this.initializeFrame = requestAnimationFrame(() => this.initialize());
    }

    disconnectedCallback() {
      if (this.initializeFrame) cancelAnimationFrame(this.initializeFrame);
      if (this.image) {
        this.image.removeEventListener('load', this.onImageLoad);
        this.image.removeEventListener('error', this.onImageError);
      }
      this.initialized = false;
    }

    initialize() {
      if (!this.isConnected || this.initialized) return;
      this.initialized = true;
      this.image = this.querySelector('[data-led-preview-image]');
      this.canvas = this.querySelector('[data-led-preview-composite]');
      this.fallback = this.querySelector('[data-led-preview-fallback]');
      const Renderer = window.LEDTextPreviewRenderer;
      if (!Renderer || !window.BottomTextSingleLineFont || !this.image || !this.canvas) {
        this.showUnavailable();
        return;
      }
      this.renderer = new Renderer();
      this.configuration = this.readConfiguration();
      if (!this.configuration) {
        this.showUnavailable();
        return;
      }
      this.onImageLoad = () => this.drawPreview();
      this.onImageError = () => this.showUnavailable();
      this.image.addEventListener('load', this.onImageLoad);
      this.image.addEventListener('error', this.onImageError);
      this.image.src = this.configuration.image;
      if (this.image.complete && this.image.naturalWidth) this.drawPreview();
      document.fonts?.ready.then(() => {
        if (!this.isConnected) return;
        this.renderer.nativeTextMeasureCache.clear();
        this.renderer.glyphGeometryCache.clear();
        if (this.image.complete && this.image.naturalWidth) this.drawPreview();
      });
    }

    readConfiguration() {
      let value = null;
      if (this.dataset.preview) {
        try {
          value = JSON.parse(this.dataset.preview);
        } catch {
          return null;
        }
        if (value?.v !== 1) return null;
        this.dataset.previewSource = 'saved';
      } else {
        if (this.dataset.artworkConfigured !== 'true') return null;
        let profile;
        try { profile = JSON.parse(this.dataset.artworkProfile || 'null'); } catch { profile = null; }
        const size = String(this.dataset.size || '').match(/(20|30|35|40|45|50|60)\s*in/i);
        value = {
          v: 1,
          image: this.dataset.image,
          aspect: Number(this.dataset.imageAspect) || 1,
          fit: 'contain',
          position: '50% 50%',
          text: this.dataset.text,
          color: this.dataset.color,
          size: size ? Number(size[1]) : 0,
          layout: this.dataset.layout,
          artwork: [this.dataset.centerX, this.dataset.centerY, this.dataset.artworkWidth, this.dataset.artworkHeight].map(Number),
          profile
        };
        this.dataset.previewSource = 'legacy';
      }
      const Renderer = window.LEDTextPreviewRenderer;
      const text = String(value.text || '').trim();
      const size = Number(value.size);
      if (!Renderer.sizeRules[size] || !text || text.length > 16 || /[^\x20-\x7E]/.test(text)) return null;
      if (!['horizontal', 'round', 'vertical'].includes(value.layout)) return null;
      if (!Array.isArray(value.artwork) || value.artwork.length !== 4
        || !value.artwork.every((number) => Number.isFinite(number) && number >= 0 && number <= 1)
        || value.artwork[2] < 0.05 || value.artwork[3] < 0.05) return null;
      let image;
      try {
        image = new URL(value.image, window.location.href);
        const host = image.hostname.toLowerCase();
        const storeOrigin = this.dataset.storeUrl ? new URL(this.dataset.storeUrl).origin : window.location.origin;
        const allowedHost = image.origin === window.location.origin || image.origin === storeOrigin
          || host === 'cdn.shopify.com' || host.endsWith('.shopifycdn.com')
          || host.endsWith('.myshopify.com');
        if (image.protocol !== 'https:' || !allowedHost || image.username || image.password) return null;
      } catch {
        return null;
      }
      let profile = value.profile;
      if (!Array.isArray(profile) || profile.length < 2 || profile.length > 100
        || !profile.every((point, index) => Array.isArray(point) && point.length === 2
          && point.every((number) => Number.isFinite(number) && number >= 0 && number <= 1)
          && (index === 0 || point[0] > profile[index - 1][0]))
        || profile[0][0] !== 0 || profile[profile.length - 1][0] !== 1) {
        profile = [[0, 1], [1, 1]];
      }
      const aspect = Number(value.aspect);
      const position = String(value.position || '').match(/^(-?[\d.]+)%\s+(-?[\d.]+)%$/);
      return {
        ...value,
        text,
        size,
        image: image.href,
        aspect: Number.isFinite(aspect) && aspect >= 0.4 && aspect <= 2.5 ? aspect : 1,
        fit: ['contain', 'cover', 'fill'].includes(value.fit) ? value.fit : 'contain',
        position: position ? [Number(position[1]) / 100, Number(position[2]) / 100] : [0.5, 0.5],
        profile
      };
    }

    drawPreview() {
      if (!this.isConnected || !this.configuration || !this.image.naturalWidth) return;
      try {
        const config = this.configuration;
        const Renderer = window.LEDTextPreviewRenderer;
        const rules = Renderer.sizeRules[config.size];
        const state = {
          text: config.text,
          layout: config.layout,
          rules,
          color: {
            name: config.color,
            value: Renderer.productColors[String(config.color).toLowerCase()] || Renderer.productColors.pink
          },
          textLayout: this.renderer.getTextLayout(config.text, rules)
        };
        this.renderer.artworkJoinProfile = config.profile;
        const width = 480;
        const height = Math.ceil(width / config.aspect);
        const geometry = this.renderer.getPreviewGeometry(width, height, state, config.artwork);
        const overlay = document.createElement('canvas');
        this.renderer.renderLayoutPreview({ querySelector: () => overlay }, state, geometry);
        const composition = document.createElement('canvas');
        composition.width = overlay.width;
        composition.height = overlay.height;
        const context = composition.getContext('2d');
        if (!context) throw new Error('Canvas unavailable');
        const ratio = overlay.width / width;
        context.setTransform(ratio, 0, 0, ratio, 0, 0);
        let drawWidth = width;
        let drawHeight = height;
        if (config.fit !== 'fill') {
          const scale = (config.fit === 'cover' ? Math.max : Math.min)(
            width / this.image.naturalWidth, height / this.image.naturalHeight);
          drawWidth = this.image.naturalWidth * scale;
          drawHeight = this.image.naturalHeight * scale;
        }
        context.drawImage(this.image,
          (width - drawWidth) * config.position[0], (height - drawHeight) * config.position[1],
          drawWidth, drawHeight);
        context.drawImage(overlay, 0, 0, width, height);
        const crop = this.getPreviewCrop(overlay, geometry);
        this.canvas.width = this.canvas.height = Math.ceil(480 * ratio);
        const thumbnail = this.canvas.getContext('2d');
        if (!thumbnail) throw new Error('Canvas unavailable');
        const scale = this.canvas.width / Math.max(crop.width, crop.height);
        const destinationWidth = crop.width * scale;
        const destinationHeight = crop.height * scale;
        thumbnail.drawImage(composition,
          crop.x * ratio, crop.y * ratio, crop.width * ratio, crop.height * ratio,
          (this.canvas.width - destinationWidth) / 2, (this.canvas.height - destinationHeight) / 2,
          destinationWidth, destinationHeight);
        this.dataset.previewCrop = JSON.stringify(crop);
        this.dataset.renderedText = config.text;
        this.dataset.renderedLayout = config.layout;
        this.dataset.previewRevision = '2026-09-28.6';
        this.dataset.ready = 'true';
      } catch {
        this.showUnavailable();
      }
    }

    getPreviewCrop(overlay, geometry) {
      const { width, height, artwork } = geometry;
      let left = artwork.centerX - artwork.width / 2;
      let right = artwork.centerX + artwork.width / 2;
      let top = artwork.centerY - artwork.height / 2;
      let bottom = artwork.centerY + artwork.height / 2;
      const pixels = overlay.getContext('2d').getImageData(0, 0, overlay.width, overlay.height).data;
      const scaleX = width / overlay.width;
      const scaleY = height / overlay.height;
      for (let y = 0; y < overlay.height; y += 1) {
        for (let x = 0; x < overlay.width; x += 1) {
          if (pixels[(y * overlay.width + x) * 4 + 3] <= 8) continue;
          left = Math.min(left, x * scaleX);
          right = Math.max(right, (x + 1) * scaleX);
          top = Math.min(top, y * scaleY);
          bottom = Math.max(bottom, (y + 1) * scaleY);
        }
      }
      left = Math.max(0, Math.min(width, left));
      right = Math.max(left, Math.min(width, right));
      top = Math.max(0, Math.min(height, top));
      bottom = Math.max(top, Math.min(height, bottom));
      const contentSide = Math.max(right - left, bottom - top);
      const availableSide = Math.min(width, height);
      if (contentSide > availableSide) return { x: 0, y: 0, width, height };
      const side = Math.min(availableSide, Math.max(1, contentSide * 1.3));
      const x = Math.max(0, Math.min(width - side, (left + right - side) / 2));
      const y = Math.max(0, Math.min(height - side, (top + bottom - side) / 2));
      return { x, y, width: side, height: side };
    }

    showUnavailable() {
      this.dataset.ready = 'false';
      this.dataset.previewFailed = 'true';
      if (this.fallback) this.fallback.textContent = this.dataset.unavailableMessage || this.fallback.textContent;
    }
  }

  customElements.define('led-cart-preview', LEDCartPreview);
})();
